import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Screen } from "@/components/layout/Screen";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PhotoGrid, Photo } from "@/components/features/PhotoGrid";
import { PhotoDetailView } from "@/components/features/PhotoDetailView";
import { getAuthenticatedClient, getCurrentUserId } from "@/services/client";
import { signStorageMap } from "@/features/audio/signedAudio";
import { formatDistanceToNow, parseISO } from "date-fns";
import {
  Camera,
  Grid3X3,
  LayoutGrid,
  Images,
  TrendingUp,
  Bug,
  Cloud,
  Loader2,
} from "lucide-react";

// Simple Mode hook with fallback
let useSimpleMode: () => { isSimpleMode: boolean };
try {
  const context = require("@/contexts/SimpleModeContext");
  useSimpleMode = context.useSimpleMode;
} catch {
  useSimpleMode = () => ({ isSimpleMode: false });
}

export type MediaItem = Photo;

export const PhotosScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isSimpleMode } = useSimpleMode();
  const userId = getCurrentUserId();
  const [activeTab, setActiveTab] = useState("all");
  const [columns, setColumns] = useState<2 | 3>(3);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Fetch real photos from Supabase
  const { data: photos = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['photos-screen', userId],
    queryFn: async () => {
      if (!userId) return [];
      const client = getAuthenticatedClient();
      const { data, error } = await client
        .from('photos')
        .select('*')
        .eq('user_id', userId)
        .is('deleted_at', null)
        .eq('upload_status', 'complete')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // FIX-CCS-PRIVACY-COMPLETE: photos bucket is PRIVATE — batch-sign paths via the PIN session.
      const withPath = (data || []).filter(p => p.storage_path);
      const signed = await signStorageMap('photos', withPath.flatMap(p => [p.storage_path, p.thumbnail_path]));
      return withPath.map((p): MediaItem => {
        const fullUrl = signed[p.storage_path] || '';
        const thumbnailUrl = (p.thumbnail_path && signed[p.thumbnail_path]) || fullUrl;

        return {
          id: p.id,
          url: fullUrl,
          thumbnailUrl: thumbnailUrl,
          category: p.photo_category || 'other',
          date: formatDistanceToNow(parseISO(p.created_at), { addSuffix: true }),
          location: p.gps_lat && p.gps_lng ? `${Number(p.gps_lat).toFixed(4)}, ${Number(p.gps_lng).toFixed(4)}` : t('photos.unknown', { defaultValue: 'Unknown' }),
          aiAnalysis: p.ai_analysis as Photo['aiAnalysis'],
        };
      });
    },
    enabled: !!userId,
    refetchOnMount: 'always',
  });

  const categories = [
    { id: "all", icon: Images },
    { id: "growth", icon: TrendingUp },
    { id: "pest", icon: Bug },
    { id: "weather", icon: Cloud },
  ];

  const categoryMap: Record<string, string[]> = {
    "growth": ["growth", "flowers"],
    "harvest": ["harvest"],
    "pest": ["pests", "disease"],
    "weather": ["field", "other"],
  };

  const filteredMedia = activeTab === "all"
    ? photos
    : photos.filter(p => categoryMap[activeTab]?.includes(p.category) || p.category.toLowerCase().includes(activeTab));

  const handlePhotoClick = (photo: Photo, index: number) => {
    setSelectedPhoto(photo);
    setSelectedIndex(index);
  };

  const renderMediaItem = (item: MediaItem) => {
    return (
      <div className="relative w-full h-full">
        <img
          src={item.thumbnailUrl}
          alt={item.category}
          className="w-full h-full object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/placeholder.svg';
          }}
        />
      </div>
    );
  };

  if (isLoading) {
    return (
      <Screen showTopBar showBottomNav>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Screen>
    );
  }

  if (isError) {
    return (
      <Screen showTopBar showBottomNav>
        <div className="flex flex-col items-center justify-center h-64 gap-2">
          <p className="text-sm text-muted-foreground">{t('photos.loadError', 'Unable to load photos')}</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            {t('common.retry', 'Try again')}
          </Button>
        </div>
      </Screen>
    );
  }

  if (photos.length === 0) {
    return (
      <Screen
        showTopBar
        showBottomNav
        topBarProps={{
          title: t('home.recentPhotos', 'Recent Photos'),
          showBack: true,
          onBack: () => navigate(-1),
        }}
      >
        <div className="flex flex-col items-center justify-center h-64 p-4">
          <Camera className="w-16 h-16 text-muted-foreground mb-4" />
          <p className="text-muted-foreground text-center simple-hide">
            {t('photos.noPhotosYet', 'No photos yet. Take your first farm photo!')}
          </p>
          <Button onClick={() => navigate('/camera')} className="mt-4">
            <Camera className="w-4 h-4 mr-2" />
            {t('photos.takePhoto', 'Take Photo')}
          </Button>
        </div>
      </Screen>
    );
  }

  return (
    <>
      <Screen
        showTopBar
        showBottomNav
        topBarProps={{
          title: t('home.recentPhotos', 'Recent Photos'),
          showBack: true,
          onBack: () => navigate(-1),
          rightAction: (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/camera")}
              className="text-white hover:bg-white/20"
            >
              <Camera className="w-5 h-5" />
            </Button>
          ),
        }}
      >
        <div className="p-4 space-y-4">
          {/* Stats & Layout Controls */}
          <Card elevation="medium" className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Camera className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{photos.length}</p>
                  <p className="text-xs text-muted-foreground simple-hide">{t('common.totalPhotos', 'Total Photos')}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setColumns(2)}
                  className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                    columns === 2
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                  aria-label="2 columns"
                >
                  <LayoutGrid className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setColumns(3)}
                  className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                    columns === 3
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                  aria-label="3 columns"
                >
                  <Grid3X3 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </Card>

          {/* Category Filter Icons */}
          <div className="flex items-center justify-center gap-3">
            {categories.map((cat) => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveTab(cat.id)}
                  className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all active:scale-95 ${
                    activeTab === cat.id
                      ? "bg-primary text-primary-foreground shadow-md"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                  aria-label={cat.id}
                >
                  <Icon className="w-6 h-6" />
                </button>
              );
            })}
          </div>

          {/* Media Grid */}
          <div className={`grid gap-1 ${columns === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
            {filteredMedia.map((item, index) => (
              <div
                key={item.id}
                onClick={() => handlePhotoClick(item, index)}
                className="relative aspect-square cursor-pointer overflow-hidden rounded-lg hover:opacity-90 transition-opacity"
              >
                {renderMediaItem(item)}
              </div>
            ))}
          </div>

          {/* Empty State for Filtered */}
          {filteredMedia.length === 0 && activeTab !== "all" && (
            <div className="text-center py-8">
              <Images className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground simple-hide">{t('common.noResults', 'No results')}</p>
              <Button
                variant="link"
                onClick={() => setActiveTab("all")}
                className="mt-2"
              >
                <Images className="w-4 h-4 mr-1" />
                <span className="simple-hide">{t('common.viewAll', 'View All')}</span>
              </Button>
            </div>
          )}
        </div>
      </Screen>

      {/* Photo Detail View */}
      {selectedPhoto && (
        <PhotoDetailView
          photos={filteredMedia}
          currentIndex={selectedIndex}
          onClose={() => setSelectedPhoto(null)}
        />
      )}
    </>
  );
};
